import {Injectable} from '@angular/core';
import {take, takeUntil, tap, map, shareReplay, first} from "rxjs/operators";
import {ListResponse} from "../../services/api/response-types";
import {Event as WireEvent} from "../../_models/event";
import * as utils from "../../lib/utils";
import {BehaviorSubject, forkJoin, Observable, of, ReplaySubject, Subject} from "rxjs";
import {Series} from '../../_models/series';
import {ApiService} from '../../services/api/api.service';
import {EventService} from "../../services/event.service";
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {IDMap, KeyMap, ModelID} from "../../_typing/generic-types";
import {SeriesAuditStatistics} from "./series-audit-history.component";

@Injectable({
    providedIn: 'root'
})
export class SeriesAuditHistoryService {
    private onDestroy: Subject<void> = new Subject();

    constructor(private api: ApiService,
                private eventService: EventService) {
    }

    $commentsSubject = new Subject<ListResponse<WireEvent>>();
    $comments = this.$commentsSubject.asObservable();
    comments: WireEvent[];

    $auditHistorySubject = new Subject<any[]>();
    $auditHistoryData = this.$auditHistorySubject.asObservable();
    $auditSubHourlyHistorySubject = new Subject<any[]>();
    $auditSubHourlyHistoryData = this.$auditSubHourlyHistorySubject.asObservable();
    $consolidatedAuditHistorySubject = new Subject<any[]>();
    $consolidatedAuditHistoryData = this.$consolidatedAuditHistorySubject.asObservable();
    $seriesStatisticsSubject = new Subject<SeriesAuditStatistics>();
    $seriesStatistics = this.$seriesStatisticsSubject.asObservable();
    $currentHour: ReplaySubject<string> = new ReplaySubject(1);

    seriesStatistics: KeyMap<SeriesAuditStatistics> = {};

    onSeriesChanged(series: Series, dtp: IDateTimePeriod, table_view = true) {
        forkJoin(this.getSeriesAuditHistoryRequest(series, dtp), this.getCommentsRequest(series, dtp))
            .pipe(first(), takeUntil(this.onDestroy))
            .subscribe(([audit_data, comment_data]) => {
                if (!audit_data[series.id]) {
                    this.$auditHistorySubject.next([]);
                    return;
                }
                const audit_history_data = this.processSeriesAuditHistoryData(audit_data[series.id]);
                this.$auditHistorySubject.next(audit_history_data);
            })
    }

    getConsolidatedSeriesAuditHistory(series: Series, dtp: IDateTimePeriod) {
        forkJoin(this.getConsolidatedSeriesAuditHistoryRequest(series, dtp), this.getCommentsRequest(series, dtp))
            .pipe(first(), takeUntil(this.onDestroy))
            .subscribe(([audit_data, comment_data]) => {
                if (!audit_data[series.id]) {
                    this.$consolidatedAuditHistorySubject.next([]);
                    return;
                }
                const audit_history_data = this.processSeriesAuditHistoryData(audit_data[series.id]);
                this.$consolidatedAuditHistorySubject.next(audit_history_data);
            })
    }

    getSubHourlySeriesAuditHistory(series: Series, start: Date, end: Date) {
        this.getSeriesSubHourlyAuditHistoryRequest(series, start, end)
            .pipe(first(), takeUntil(this.onDestroy))
            .subscribe(audit_data => {
                if (!audit_data[series.id]) {
                    this.$auditSubHourlyHistorySubject.next([]);
                    return;
                }
                const audit_history_data = this.processSeriesAuditHistoryData(audit_data[series.id]);
                this.$auditSubHourlyHistorySubject.next(audit_history_data);
            })
    }

    getSeriesTimeStatistics(seriesId: ModelID, start: Date, end: Date) {
        let params = {
            series_id: seriesId,
            start: start.toISOString(),
            end: end.toISOString(),
        }
        const timestamp = this.getTimestampKey(start);
        const key = seriesId + timestamp;
        let $statistics: Observable<SeriesAuditStatistics>;
        if (this.seriesStatistics[key]) {
            $statistics = of(this.seriesStatistics[key]);
        } else {
            $statistics = this.api.get('/api/seriesAuditHistorySubHourlyStatistics?' + utils.httpParamSerializer(params)).pipe(
                map((result) => {
                    this.seriesStatistics[key] = result?.[seriesId]?.[timestamp];
                    if (this.seriesStatistics[key]) {
                        this.seriesStatistics[key]['id'] = key;
                    }
                    return this.seriesStatistics[key];
                })
            );
        }
        $statistics.pipe(first(), tap(() => {
            this.$seriesStatisticsSubject.next(this.seriesStatistics[key]);
        }), takeUntil(this.onDestroy)).subscribe();
    }

    private getSeriesAuditHistoryRequest(series: Series, dtp: IDateTimePeriod) {
        let params = {
            series_id: series.id,
            start: dtp.start.toISOString(),
            end: dtp.end.toISOString(),
        }
        return this.api.get('/api/seriesAuditHistory?' + utils.httpParamSerializer(params));
    }

    private getConsolidatedSeriesAuditHistoryRequest(series: Series, dtp: IDateTimePeriod) {
        let params = {
            series_id: series.id,
            start: dtp.start.toISOString(),
            end: dtp.end.toISOString(),
        }
        return this.api.get('/api/v2/seriesAuditHistory?' + utils.httpParamSerializer(params));
    }

    private getSeriesSubHourlyAuditHistoryRequest(series: Series, start: Date, end: Date, pageSize: number = 10, page: number = 1) {
        let params = {
            series_id: series.id,
            start: start.toISOString(),
            end: end.toISOString(),
            batch_size: pageSize,
            batch: page - 1
        };
        return this.api.get('/api/seriesAuditHistorySubHourly?' + utils.httpParamSerializer(params));
    }

    private getCommentsRequest(series: Series, dtp: IDateTimePeriod) {
        return this.eventService.getEventsByBaseType('event', dtp.start, dtp.end, [series])
            .pipe(first(), takeUntil(this.onDestroy),
                tap((comments: ListResponse<WireEvent>) => {
                    this.comments = comments.data;
                }));
    }

    private processSeriesAuditHistoryData(times, table_view = true) {
        if (table_view === true) {
            let data: any = [];
            Object.keys(times).map(key => {
                for (let index = 0; index < times[key].length; index++) {
                    let item = times[key][index];
                    item['id'] = `${key}_${index}`;
                    item['timestamp'] = item.data_point_timestamp ?? key;
                    item['comments'] = this.getItemComments(item['changed_on']);
                    data.push(item);
                }
            });
            return data;
        } else {
            let paged_history = [];
            Object.keys(times).forEach(time => {
                paged_history.push(times[time]);
            });
            return this.getPaged(paged_history)
        }
    }

    private getItemComments(changed_on) {
        return this.comments.filter(c => c.attributes.start <= changed_on && c.attributes.end >= changed_on)
            .map(comment => comment.attributes.comment);
    }

    private getPaged(paged_history) {
        const ctrl = this;
        for (let i = 0; i < paged_history.length; i++) {
            let item = paged_history[i][0];
            let prev = paged_history[i - 1];
            if (prev) {
                prev = prev[0];
            }
            let str = '';
            if (!prev && item.value) {
                str = "Added <b>" + item.value + "</b>";
            }
            if (prev && prev.value && item.value) {
                str = "Replaced <b>" + prev.value + "</b> with <b>" + item.value + "</b>";
            }
            if (prev && prev.value && !item.value) {
                str = "Deleted <b>" + prev.value + "</b>";
            }
            item['value_string'] = str;
        }
        return paged_history;
    }

    getTimestampKey(date: Date): string {
        return date.toISOString().slice(0, 19);
    }

    ngOnDestroy(): void {
        if (this.onDestroy) {
            this.onDestroy.next();
            this.onDestroy.complete();
        }
    }
}
